import { TextMessageTemplates } from "./TextMessageTemplates";
import { TextShortcode } from "./TextShortcode";
import {
    DoctorFollowUpNextTouch,
    FaxRoutingRule,
    PriorAuthNextTouch,
    PriorAuthStatusReason,
} from "./PriorAuthorization";
import { Speciality } from "./Speciality";
import { EmailTemplates } from "@/models/EmailTemplates";
import { RefillStatusCode } from "@/models/Prescription";
import { EntityAlertConfigurations } from "@/models/AlertConfiguration";
import { CaseWatcher, CaseAssignment } from "@/models/Case";
import { DeaScheduleExpirationEntry } from "@/models/DeaScheduleExpirationEntry";

export interface CaseProblemConfig {
    name: string;
    watchers: CaseWatcher[] | null;
    assignment?: CaseAssignment | null;
    nextTouchDays?: number | null;
}

export interface CaseCategory {
    name: string;
    order: number;
}

export interface FaxTemplateOverride {
    imageId: number;
    fileName?: string;
}

export interface InventoryLotStatus {
    name: string;
    isEligibleForFilling: boolean;
}

export interface PatientDashboardConfiguration {
    defaultLayout: string | null;
    hiddenCards: string[] | null;
}

export interface ForbiddenPhoneNumber {
    number: string;
    reason: string;
}

export interface QuickDialNumber {
    label: string;
    number: string;
}

export interface AutoOutboundConfiguration {
    contactDelay: number;
    fillDateMaxAge: number;
    twilioFlow: string;
}

export class AutoOutboundConfiguration {
    constructor(obj?: AutoOutboundConfiguration) {
        this.contactDelay = 0;
        this.fillDateMaxAge = 0;
        this.twilioFlow = "";
        if (obj) {
            Object.assign(this, obj);
        }
    }
}

export interface AutoOutboundStatusConfiguration extends AutoOutboundConfiguration {
    status: RefillStatusCode;
    subStatus: number | null;
}

export class AutoOutboundStatusConfiguration {
    constructor(obj?: AutoOutboundStatusConfiguration) {
        this.status = RefillStatusCode.AutoRefillable;
        this.subStatus = null;
        this.contactDelay = 0;
        this.fillDateMaxAge = 0;
        this.twilioFlow = "";
        if (obj) {
            Object.assign(this, obj);
        }
    }
}

export interface Configuration {
    refillTextDays: number;
    oldScriptRefillTextDays: number;
    licenseExpirationAlertDays: number;
    ordersAllowPV1Prescriptions: boolean;
    requireExplicitVerificationPV1: boolean;
    requireExplicitVerificationPV2: boolean;
    hideDispensingFields: boolean;
    suppressEscriptDiagnosisAlert: boolean;
    autoRefillPromptStatement: string;
    autoRefillPromptStatementSpanish: string;
    autoRefillPromptStatementRussian: string;
    autoRefillPromptStatementSomali: string;
    autoRefillPromptStatementArabic: string;
    autoRefillPromptStatementChineseSimplified: string;
    autoRefillPromptStatementVietnamese: string;
    autoRefillPromptStatementFarsi: string;
    autoRefillPromptStatementKorean: string;
    autoRefillPromptStatementRomanian: string;
    autoRefillPromptStatementSwahili: string;
    autoRefillPromptStatementBurmese: string;
    autoRefillPromptStatementNepali: string;
    autoRefillPromptStatementAmharic: string;
    autoRefillPromptStatementPashto: string;
    autoCaseAppNewPatientCreation: boolean;
    autoCaseTextsNoValidNumbers: boolean;
    autoCaseTextsPatientContactFailure: boolean;
    autoCaseTextsFailAutoRefill: boolean;
    partialPaymentTextThresholdAmount: number;
    npcTextThresholdAmount: number;
    infinitrakApiKey: string;
    disableMedGuidePrinting: boolean;
    doNotPrintShippingTicket: boolean;
    inventoryReasons: string[];
    textMessageTemplates: TextMessageTemplates;
    emailTemplates: EmailTemplates;
    readyForPickupTexts: boolean;
    disableWebNotifications: boolean;
    orderCancellationReasons: string[];
    requireLotsOnInventoryBatches: boolean;
    requireExpirationOnReturnBatch: boolean;
    blockDeactivatingDrugIfInventoryOnHand: boolean;
    blockC2PatientAppOrders: boolean;
    inventoryBatchSubmitEndpoint: string;
    allowAutorefillWithoutNotification: boolean;
    allowAutorefillWithoutPayment: boolean;
    onHoldSubStatuses: any[];
    discontinuedSubStatuses: any[];
    faxCoverPage: boolean;
    patientAppCheckoutAttestation: string;
    requireExplicitDur: number | null;
    requireDurNotes: boolean;
    enableRTPB: boolean;
    patientDocumentTags: string[];
    saveTransferDocumentToPatientDocuments: boolean;
    hideIncompleteConsultationsOnPrintQueue: boolean;
    hideIncompleteAnswersOnPrintQueue: boolean;
    transferReasons: any[];
    patientAssistanceProgramRejectReasons: string[];
    priorAuthStatusReasons: PriorAuthStatusReason[];
    priorAuthNextTouchDays: PriorAuthNextTouch[];
    priorAuthNoteTemplates: string[];
    doctorFollowUpNextTouchDays: DoctorFollowUpNextTouch[];
    doctorFollowUpMaxDays: number;
    papNextTouchDays: any[];
    faxRoutingRules: FaxRoutingRule[];
    planTypeCanBeUnknown: boolean;
    caseProblems: CaseProblemConfig[];
    caseCategories?: CaseCategory[];
    npcTextHours: number;
    awaitingInsuranceHours: number;
    outOfNetworkHours: number;
    initialRefillDays: number[];
    noRefillsRemainingCadenceDays?: number[];
    deliveryConfirmationDays: number[];

    // AR / Patient Billing (SD-4534)
    enableAR: boolean;
    arDefaultCreditLimit: number | null;
    arStatementClosingDay: number | null;
    arAgingBucketDays: number[];
    arDefaultStatementDeliveryMethods: number[];
    arPortalPaymentCC: boolean;
    enableArAutoPay: boolean;
    arStatementReasonCodes: string[];
    arStatementPaymentInstructions: string | null;
    arStatementFooter: string | null;
    pharmacistOnlyNotes: boolean;
    excludeRefillRequestsFromNotes: boolean;
    enableInternalOnlyNotes: boolean;
    // awaitingInsuranceVoicemailDays: number; Obsolete, use new auto outbound config
    createTaskOnMPPPClaimFromTaskTemplateId: number | null;
    createTaskOnMPPPEligibilityFromTaskTemplateId: number | null;
    autoRefillNextProcessDateDefaultCadence: number | null;
    autoRefillNextProcessDateDefaultCadenceType: number | null;
    autoRefillCopayDifferenceAllowance: number;
    autoRefillAutoProcessAuditChance: number;
    dispenseErrorLabel: string;
    prescriberFollowUpNoteTemplates: string[];
    specialties: Speciality[];
    allowShippingControlledWithoutPdmp: boolean;
    autoOutboundStatusConfigurations: AutoOutboundStatusConfiguration[];
    autoOutboundPartialPaymentConfiguration: AutoOutboundConfiguration;
    autoOutboundDeliveryConfirmationConfiguration: AutoOutboundConfiguration;
    providerFollowUpResponseTeamId: number | null;
    voicemailNotificationTeamId: number | null;
    parseMiddleNameFromEscript: boolean;
    rxTransferDeniedSubStatusId?: number | null;
    rxTransferDeniedTaskTemplateId?: number | null;
    providerPortalAccountCreationTaskTemplateId?: number | null;
    disableFutureWrittenDates: boolean;
    entityAlertConfigurations: EntityAlertConfigurations;
    faxTemplateOverrides?: Record<string, FaxTemplateOverride>;
    requirePharmacistOnExternalTransfers: boolean;
    requireTransferReasonOnOutboundTransfers: boolean;
    includePrescriptionImageOnExternalTransfers: boolean;
    useLastForDiffCheck: boolean;
    allowMultipleTaskAssignments: boolean;
    requireTaskAssignment: boolean;
    disableAllAI: boolean;
    disableInsuranceParsingAI: boolean;
    enforceInsuranceAllowed: boolean;
    hideEligibilityAndRtpbWhenInsuranceNotAllowed: boolean;
    autoAssignResolvedCasesToComplianceTeam: boolean;
    enableEquivalentDrugCheck: boolean;
    enableAcceptAndTransfer: boolean;
    requireEligibleProgramForPatient: boolean;
    pv1OnlyPrescriptionsOnOrders: boolean;
    requirePV1OnAllRefills: boolean;
    skipOrderVerificationAfterPV1: boolean;
    disableSurescriptsETransfer: boolean;
    checkMyRxCostEnabled: boolean;
    patientRightsPdfImageId?: number | null;
    accountManagerMode: AccountManagerMode;
    showAlertsOnFillingPage: boolean;
    requireCompoundNdcVerification: boolean;
    requireCompoundLotNumber: boolean;
    requireGtinVerification: boolean;
    enableShippingQualityCheck: boolean;
    notifyCaseCreatorOnResolution: boolean;
    inventoryLotStatuses: InventoryLotStatus[];
    warnOnDuplicateShipment: boolean;
    patientDashboardDefaultLayout: string | null;
    patientDashboardHiddenCards: string[] | null;
    patientDashboardConfiguration: PatientDashboardConfiguration;
    forbiddenPhoneNumbers: ForbiddenPhoneNumber[];
    quickDialNumbers: QuickDialNumber[];
    requireNextTouchDateOnHold: boolean;
    autoFinishSubscription: boolean;
    textShortcodes: TextShortcode[];
    pmpFailureTaskTeamId: number | null;
    enablePrescriberNpiValidation: boolean;
    requireDirectionsAtEntry: boolean;
    validatePrescriberAuthority: boolean;
    refillTextSendDelaySeconds: number;
    shipmentNotifySendDelaySeconds: number;
    deliveryConfirmationSendDelaySeconds: number;
    allowPatientPickup: boolean;
    enforceDriversLicenseUploadAtCheckout: boolean;
    deaScheduleExpirations: DeaScheduleExpirationEntry[];
    allowAnnotationRevert: boolean;
    bypassStoreHoursRefillTextWorker: boolean;
    bypassStoreHoursNPCTextWorker: boolean;
    bypassStoreHoursWelcomeTextWorker: boolean;
    bypassStoreHoursNoRefillsRemainingTextWorker: boolean;
    disableWelcomeTextForUnsortedEscripts: boolean;

    /** Carriers the tenant permits (FedEx/USPS/UPS/Ameriship). Null/empty = no restriction. Composes with Program.Carriers and Patient.Carriers as the broadest allow-list. */
    allowedCarriers: string[] | null;

    /** Carrier service types the tenant permits. Null/empty = no restriction. Carrier-agnostic. */
    allowedServiceTypes: string[] | null;

    /** Carrier service add-ons the tenant permits. Null/empty = no restriction. Carrier-agnostic. */
    allowedServiceAddOns: string[] | null;

    /** Opt-in: show the Box Barcodes button + package dimension fields on the Shipping page. Default off. */
    enableBoxBarcodes: boolean;
    enforceEscriptEffectiveDate: boolean;

    patientAppDeliverySurveyQuestionSetId: number | null;
    enablePostDeliverySurvey: boolean;
    warnOnDuplicateFaxForStoredPharmacy: boolean;

    /** Days a prescriber-portal Cognito password is valid before a forced reset. 0 disables. */
    prescriberPortalPasswordExpirationDays: number;

    /** When true, EPA creation bypasses the appsettings drug package allowlists, allowing any drug. */
    allowAllDrugsForElectronicPriorAuthorization: boolean;

    /** When true, staff can expand and view hard copy prescriptions belonging to a different patient. */
    showCrossPatientHardCopyScripts: boolean;

    enablePositiveIdentification: boolean;
    positiveIdMethods: string[] | null;
    positiveIdBiometricConsentDocumentId: number | null;

    promptGenericSubstitutionOnBrand: boolean;
    requireTaskCancellationReason: boolean;

    /**
     * Tenant-level default that suppresses the "PV1 Needed" reminder modal on the PV2 screen
     * for prescriptions where PV1 was not performed. Programs may opt into this suppression via
     * the "Disable PV1 Needed Warning" functionality flag: an explicit enable wins, while an
     * explicit disable or no override defers to this tenant default.
     */
    disablePV1NeededWarning: boolean;

    defaultIngestionQueueSelectAllForPatient: boolean;

    /**
     * Idle time (in minutes) before the admin frontend warns, locks the screen, and signs the user
     * out. 0 disables the inactivity timeout entirely. Enforced by a SharedWorker-managed timer
     * shared across all of the user's open tabs.
     */
    frontEndInactivityTimeoutMinutes: number;

    /**
     * When true, the Doctor Record form exposes a "Requires Supervising Physician" checkbox
     * per prescriber. When the checkbox is on, the Supervising Physician section (Name +
     * Credentials) appears and is required before save. Tenants with the toggle off see no
     * supervising-physician UI at all.
     */
    enableSupervisingPhysician: boolean;
    /**
     * When true, the Rx form exposes diagnosis (ICD10) selection at the prescription level.
     * Selecting codes is always optional — never required to save an Rx. Off by default (opt-in).
     */
    enableRxLevelDiagnosis: boolean;
}

export enum AccountManagerMode {
    Disabled = 0,
    SinglePrescriber = 1,
    AllPrescribersWithNpi = 2
}

export class Configuration {
    constructor(obj?: Configuration) {
        this.refillTextDays = 0;
        this.oldScriptRefillTextDays = 0;
        this.ordersAllowPV1Prescriptions = false;
        this.requireExplicitVerificationPV1 = false;
        this.requireExplicitVerificationPV2 = false;
        this.hideDispensingFields = false;
        this.suppressEscriptDiagnosisAlert = false;
        this.autoRefillPromptStatement = "";
        this.autoRefillPromptStatementSpanish = "";
        this.autoRefillPromptStatementRussian = "";
        this.autoRefillPromptStatementSomali = "";
        this.autoRefillPromptStatementArabic = "";
        this.autoRefillPromptStatementChineseSimplified = "";
        this.autoRefillPromptStatementVietnamese = "";
        this.autoRefillPromptStatementFarsi = "";
        this.autoRefillPromptStatementKorean = "";
        this.autoRefillPromptStatementRomanian = "";
        this.autoRefillPromptStatementSwahili = "";
        this.autoRefillPromptStatementBurmese = "";
        this.autoRefillPromptStatementNepali = "";
        this.autoRefillPromptStatementAmharic = "";
        this.autoRefillPromptStatementPashto = "";
        this.autoCaseAppNewPatientCreation = false;
        this.autoCaseTextsNoValidNumbers = false;
        this.autoCaseTextsPatientContactFailure = false;
        this.autoCaseTextsFailAutoRefill = false;
        this.partialPaymentTextThresholdAmount = 0;
        this.npcTextThresholdAmount = 0;
        this.infinitrakApiKey = "";
        this.disableMedGuidePrinting = false;
        this.doNotPrintShippingTicket = false;
        this.textMessageTemplates = new TextMessageTemplates();
        this.emailTemplates = new EmailTemplates();
        this.readyForPickupTexts = false;
        this.disableWebNotifications = false;
        this.requireLotsOnInventoryBatches = false;
        this.requireExpirationOnReturnBatch = false;
        this.blockDeactivatingDrugIfInventoryOnHand = false;
        this.blockC2PatientAppOrders = false;
        this.inventoryBatchSubmitEndpoint = "";
        this.allowAutorefillWithoutNotification = false;
        this.allowAutorefillWithoutPayment = false;
        this.faxCoverPage = false;
        this.patientAppCheckoutAttestation = "";
        this.requireExplicitDur = null;
        this.requireDurNotes = false;
        this.enableRTPB = false;
        this.saveTransferDocumentToPatientDocuments = true;
        this.hideIncompleteConsultationsOnPrintQueue = false;
        this.doctorFollowUpMaxDays = 7;
        this.planTypeCanBeUnknown = false;
        this.npcTextHours = 24;
        this.awaitingInsuranceHours = 24;
        this.outOfNetworkHours = 24;
        this.enableAR = false;
        this.arDefaultCreditLimit = null;
        this.arStatementClosingDay = null;
        this.arPortalPaymentCC = true;
        this.enableArAutoPay = false;
        this.arStatementPaymentInstructions = null;
        this.arStatementFooter = null;
        this.pharmacistOnlyNotes = true;
        this.excludeRefillRequestsFromNotes = false;
        this.enableInternalOnlyNotes = false;
        this.createTaskOnMPPPClaimFromTaskTemplateId = null;
        this.createTaskOnMPPPEligibilityFromTaskTemplateId = null;
        this.autoRefillCopayDifferenceAllowance = 0;
        this.autoRefillAutoProcessAuditChance = 0;
        this.allowShippingControlledWithoutPdmp = false;
        this.parseMiddleNameFromEscript = true;
        this.rxTransferDeniedSubStatusId = null;
        this.rxTransferDeniedTaskTemplateId = null;
        this.providerPortalAccountCreationTaskTemplateId = null;
        this.voicemailNotificationTeamId = null;
        this.disableFutureWrittenDates = false;
        this.autoOutboundPartialPaymentConfiguration = new AutoOutboundConfiguration();
        this.autoOutboundDeliveryConfirmationConfiguration = new AutoOutboundConfiguration();
        this.entityAlertConfigurations = {};
        this.faxTemplateOverrides = {};
        this.requirePharmacistOnExternalTransfers = false;
        this.requireTransferReasonOnOutboundTransfers = false;
        this.includePrescriptionImageOnExternalTransfers = true;
        this.useLastForDiffCheck = false;
        this.allowMultipleTaskAssignments = false;
        this.requireTaskAssignment = false;
        this.disableAllAI = false;
        this.disableInsuranceParsingAI = false;
        this.enforceInsuranceAllowed = false;
        this.hideEligibilityAndRtpbWhenInsuranceNotAllowed = false;
        this.enableEquivalentDrugCheck = false;
        this.enableAcceptAndTransfer = false;
        this.requireEligibleProgramForPatient = false;
        this.pv1OnlyPrescriptionsOnOrders = false;
        this.requirePV1OnAllRefills = false;
        this.skipOrderVerificationAfterPV1 = false;
        this.disableSurescriptsETransfer = false;
        this.checkMyRxCostEnabled = false;
        this.patientRightsPdfImageId = null;
        this.accountManagerMode = AccountManagerMode.Disabled;
        this.showAlertsOnFillingPage = false;
        this.requireCompoundNdcVerification = false;
        this.requireCompoundLotNumber = false;
        this.requireGtinVerification = true;
        this.enableShippingQualityCheck = false;
        this.notifyCaseCreatorOnResolution = false;
        this.warnOnDuplicateShipment = false;
        this.patientDashboardDefaultLayout = null;
        this.patientDashboardHiddenCards = null;
        this.patientDashboardConfiguration = { defaultLayout: null, hiddenCards: null };
        this.requireNextTouchDateOnHold = false;
        this.autoFinishSubscription = true;
        this.textShortcodes = [];
        this.pmpFailureTaskTeamId = null;
        this.enablePrescriberNpiValidation = false;
        this.requireDirectionsAtEntry = false;
        this.validatePrescriberAuthority = false;
        this.refillTextSendDelaySeconds = 0;
        this.shipmentNotifySendDelaySeconds = 0;
        this.deliveryConfirmationSendDelaySeconds = 0;
        this.allowAnnotationRevert = false;
        this.allowedCarriers = null;
        this.allowedServiceTypes = null;
        this.allowedServiceAddOns = null;
        this.enableBoxBarcodes = false;
        this.enforceEscriptEffectiveDate = false;
        this.allowPatientPickup = false;
        this.enforceDriversLicenseUploadAtCheckout = false;
        this.patientAppDeliverySurveyQuestionSetId = null;
        this.enablePostDeliverySurvey = true;
        this.warnOnDuplicateFaxForStoredPharmacy = false;
        this.prescriberPortalPasswordExpirationDays = 180;
        this.allowAllDrugsForElectronicPriorAuthorization = false;
        this.showCrossPatientHardCopyScripts = false;
        this.enablePositiveIdentification = false;
        this.positiveIdMethods = null;
        this.positiveIdBiometricConsentDocumentId = null;
        this.promptGenericSubstitutionOnBrand = false;
        this.requireTaskCancellationReason = false;
        this.disablePV1NeededWarning = false;
        this.defaultIngestionQueueSelectAllForPatient = false;
        this.enableSupervisingPhysician = false;
        this.enableRxLevelDiagnosis = false;
        this.frontEndInactivityTimeoutMinutes = 0;

        if (obj) {
            Object.assign(this, obj);
            if (obj.autoOutboundStatusConfigurations) {
                this.autoOutboundStatusConfigurations = obj.autoOutboundStatusConfigurations.map(x => new AutoOutboundStatusConfiguration(x));
            }
        }

        if (!this.faxTemplateOverrides) {
            this.faxTemplateOverrides = {};
        }

        if (!this.autoRefillNextProcessDateDefaultCadenceType) {
            this.autoRefillNextProcessDateDefaultCadenceType = 0;
        }

        if (!this.initialRefillDays) {
            this.initialRefillDays = [7];
        }
        if (!this.noRefillsRemainingCadenceDays) {
            this.noRefillsRemainingCadenceDays = [];
        }
        if (!this.deliveryConfirmationDays) {
            this.deliveryConfirmationDays = [1, 7];
        }

        if (!this.arAgingBucketDays) {
            this.arAgingBucketDays = [30, 60, 90, 120];
        }
        if (!this.arDefaultStatementDeliveryMethods) {
            this.arDefaultStatementDeliveryMethods = [4];
        }
        if (!this.arStatementReasonCodes) {
            this.arStatementReasonCodes = [];
        }

        if (!this.inventoryReasons) {
            this.inventoryReasons = [
                "New Inventory",
                "Stock Check",
            ];
        }

        if (!this.textMessageTemplates) {
            this.textMessageTemplates = new TextMessageTemplates();
        }
        if (!this.emailTemplates) {
            this.emailTemplates = new EmailTemplates();
        }

        if (!this.orderCancellationReasons) {
            this.orderCancellationReasons = [];
        }

        if (!this.onHoldSubStatuses) {
            this.onHoldSubStatuses = [];
        }

        if (!this.discontinuedSubStatuses) {
            this.discontinuedSubStatuses = [];
        }

        if (!this.patientDocumentTags) {
            this.patientDocumentTags = [];
        }

        if (!this.transferReasons) {
            this.transferReasons = [];
        }

        if (!this.patientAssistanceProgramRejectReasons) {
            this.patientAssistanceProgramRejectReasons = [];
        }

        if (!this.priorAuthStatusReasons) {
            this.priorAuthStatusReasons = [];
        }

        if (!this.priorAuthNextTouchDays) {
            this.priorAuthNextTouchDays = [];
        }

        if (!this.priorAuthNoteTemplates) {
            this.priorAuthNoteTemplates = [];
        }

        if (!this.papNextTouchDays) {
            this.papNextTouchDays = [];
        }

        if (!this.caseProblems) {
            this.caseProblems = [];
        }

        if (!this.caseCategories) {
            this.caseCategories = [];
        }

        if (!this.forbiddenPhoneNumbers) {
            this.forbiddenPhoneNumbers = [];
        }

        if (!this.quickDialNumbers) {
            this.quickDialNumbers = [];
        }

        if (!this.deaScheduleExpirations) {
            this.deaScheduleExpirations = [];
        }

        if (!this.prescriberFollowUpNoteTemplates) {
            this.prescriberFollowUpNoteTemplates = [];
        }

        if (!this.specialties) {
            this.specialties = [];
        }

        if (!this.inventoryLotStatuses) {
            this.inventoryLotStatuses = [];
        }

        if (!this.autoOutboundStatusConfigurations) {
            this.autoOutboundStatusConfigurations = [];
        }
        if (!this.autoOutboundPartialPaymentConfiguration) {
            this.autoOutboundPartialPaymentConfiguration = new AutoOutboundConfiguration();
        }
        if (!this.autoOutboundDeliveryConfirmationConfiguration) {
            this.autoOutboundDeliveryConfirmationConfiguration = new AutoOutboundConfiguration();
        }

        this.bypassStoreHoursRefillTextWorker = this.bypassStoreHoursRefillTextWorker ?? false;
        this.bypassStoreHoursNPCTextWorker = this.bypassStoreHoursNPCTextWorker ?? false;
        this.bypassStoreHoursWelcomeTextWorker = this.bypassStoreHoursWelcomeTextWorker ?? false;
        this.bypassStoreHoursNoRefillsRemainingTextWorker = this.bypassStoreHoursNoRefillsRemainingTextWorker ?? false;
        this.disableWelcomeTextForUnsortedEscripts = this.disableWelcomeTextForUnsortedEscripts ?? false;
    }

    toString(): string {
        return JSON.stringify(this);
    }

    typeOf(name: string): string {
        // using a default populated Configuration object here, so that when we change the values in the inputs,
        // it doesn't think that the type has changed. (e.g., number type going blank will then switch to the string type)
        const conf = new Configuration();
        type configType = keyof typeof conf;
        const val = conf[name as configType];
        const valType = typeof val;

        if (valType != "object") return valType;
        if ((val as any) instanceof Date) return 'Date';

        return valType;
    }
}

export interface CadenceFields {
    title: string;
    groups: string[];
    type: string;
    help: string | undefined,
    options: { value: number; text: string; }[] | undefined;
    append: string | undefined;
}

export function autoRefillCadenceFields(cadenceType: number): {
    autoRefillNextProcessDateDefaultCadence: CadenceFields;
    autoRefillNextProcessDateDefaultCadenceType: CadenceFields;
} {
    return {
        autoRefillNextProcessDateDefaultCadence: {
            title: "Auto Refill Next Process Date Default Cadence",
            groups: ["Auto Refill"],
            type: "number",
            help: cadenceType === 0 ?
                "Sets the percentage of days supply used for the next process date on auto refills by default.\n\nEnter a whole number percentage from 1-100." :
                "Sets the number of days before days supply to use for the next process date on auto refills by default.\n\nEnter a whole number.",
            options: undefined,
            append: cadenceType === 0 ? "%" : undefined,
        },
        autoRefillNextProcessDateDefaultCadenceType: {
            title: "Auto Refill Next Process Date Default Cadence Type",
            groups: ["Auto Refill"],
            type: "select",
            help: undefined,
            options: [
                {
                    value: 0,
                    text: "Percent of Days Supply",
                },
                {
                    value: 1,
                    text: "Days Before",
                },
            ],
            append: undefined,
        },
    };
}
